import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Link } from "@/components/ui/link";
import * as React from "react";

import { AgentDialog } from "@/components/authoring/agent-dialog";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { deleteAgent } from "@/lib/api/endpoints";
import { useMyAgents } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { AgentView } from "@/types/api";

export default function AgentsPage() {
  const { data, isLoading, isError, error, refetch } = useMyAgents();
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AgentView | undefined>();
  const [toDelete, setToDelete] = React.useState<AgentView | undefined>();

  const del = useMutation({
    mutationFn: (slug: string) => deleteAgent(slug),
    onSuccess: async () => {
      toast.success("Agent deleted.");
      await qc.invalidateQueries({ queryKey: qk.myAgents });
      setToDelete(undefined);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };
  const openEdit = (a: AgentView) => {
    setEditing(a);
    setDialogOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Agents"
        description="Create agents and manage their versions."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New agent
          </Button>
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState title="No agents yet" hint="Create a domain, then add your first agent." />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Slug</TH>
                <TH>Domain</TH>
                <TH>Versions</TH>
                <TH className="w-24 text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {(data ?? []).map((a) => (
                <TR key={a.id}>
                  <TD className="font-medium">
                    <Link href={`/agents/${a.slug}`} className="hover:underline">
                      {a.name}
                    </Link>
                  </TD>
                  <TD className="font-mono text-xs text-muted-foreground">{a.slug}</TD>
                  <TD className="text-muted-foreground">{a.domain_name}</TD>
                  <TD>
                    <Badge variant="secondary">{a.version_count}</Badge>
                  </TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit"
                        onClick={() => openEdit(a)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={() => setToDelete(a)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <AgentDialog open={dialogOpen} onOpenChange={setDialogOpen} agent={editing} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(undefined)}
        title={`Delete agent “${toDelete?.name}”?`}
        description="This deletes the agent and all its versions. This cannot be undone."
        pending={del.isPending}
        onConfirm={() => toDelete && del.mutate(toDelete.slug)}
      />
    </div>
  );
}
